import { useEffect, useState } from "react";
import { Delete, Lock, X } from "lucide-react";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "del"];

// Full-screen 4-digit keypad used both to set the handover code and to
// unlock. Big keys — it lives on an iPad at the desk.
export const HandoverPinPad = ({ mode, onSubmit, onCancel, error, cooldownUntil, hasSavedPin, onUseSaved }) => {
  const [pin, setPin] = useState("");
  const [now, setNow] = useState(Date.now());
  const coolingSecs = cooldownUntil ? Math.max(0, Math.ceil((cooldownUntil - now) / 1000)) : 0;

  useEffect(() => {
    if (!cooldownUntil) return undefined;
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [cooldownUntil]);

  useEffect(() => { setPin(""); }, [error]);

  const press = (k) => {
    if (coolingSecs > 0) return;
    if (k === "del") { setPin((p) => p.slice(0, -1)); return; }
    if (!k || pin.length >= 4) return;
    const next = pin + k;
    setPin(next);
    if (next.length === 4) setTimeout(() => onSubmit(next), 120);
  };

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/95 text-white flex items-center justify-center p-6" data-testid="handover-pin-pad">
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2 text-emerald-400 font-black uppercase tracking-[0.2em] text-xs">
            <Lock className="w-4 h-4" /> Handover mode
          </div>
          {onCancel && (
            <button type="button" onClick={onCancel} className="p-2 rounded-lg hover:bg-zinc-800" data-testid="handover-pin-cancel">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
        <h2 className="text-2xl font-black">{mode === "set" ? "Choose a 4-digit unlock code" : "Enter your unlock code"}</h2>
        <p className="text-sm text-zinc-400 mt-1">
          {mode === "set"
            ? "You'll need this to get back to the desk once the customer has the iPad."
            : "Staff only — the customer view stays locked until you unlock it."}
        </p>

        <div className="flex justify-center gap-4 my-8" data-testid="handover-pin-dots">
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className={`w-4 h-4 rounded-full transition-colors ${i < pin.length ? "bg-emerald-400" : "bg-zinc-700"}`} />
          ))}
        </div>

        {error && coolingSecs === 0 && (
          <div className="text-center text-red-400 font-bold mb-4" data-testid="handover-pin-error">{error}</div>
        )}
        {coolingSecs > 0 && (
          <div className="text-center text-amber-300 font-bold mb-4" data-testid="handover-pin-cooldown">
            Too many tries — wait {coolingSecs}s
          </div>
        )}

        <div className="grid grid-cols-3 gap-3">
          {KEYS.map((k, i) => (
            <button
              key={i} type="button" onClick={() => press(k)}
              disabled={!k || coolingSecs > 0}
              className={`h-16 rounded-2xl text-2xl font-black transition-colors ${k ? "bg-zinc-800 hover:bg-zinc-700 active:bg-emerald-600" : "invisible"} disabled:opacity-40`}
              data-testid={k ? `handover-key-${k}` : undefined}
            >
              {k === "del" ? <Delete className="w-6 h-6 mx-auto" /> : k}
            </button>
          ))}
        </div>

        {mode === "set" && hasSavedPin && (
          <button type="button" onClick={onUseSaved} className="w-full mt-5 py-3 rounded-xl border border-zinc-700 text-zinc-300 font-bold hover:bg-zinc-800" data-testid="handover-use-saved-pin">
            Use this iPad&apos;s saved code
          </button>
        )}
      </div>
    </div>
  );
};
